import {
  Component,
  Input,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { AuthService } from "src/app/core/services/authentication.service";
import { Utils } from "src/app/core/resources/utils";
import { AbstractControl, FormControl, Validators } from "@angular/forms";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { Observable, Subscription } from "rxjs";
import { SelectGpsService } from "./select-gps.service";
import { gpsType } from "src/app/core/interfaces/gpsType";

@Component({
  selector: "app-select-gps",
  templateUrl: "./select-gps.component.html",
  styleUrls: ["./select-gps.component.scss"],
  providers: [AuthService, SelectGpsService],
})
export class SelectGpsComponent implements OnInit {
  @Input() options;
  @Input() inputFormControl: FormControl;
  @Input() validate: string;
  gpsControl = new FormControl();
  gpsSub: Subscription;
  gpsTypes: gpsType[];
  gpsProviders: Array<{ monitor: boolean, name: string, companyId: string, id: string }>;
  private readonly SMARTELOCK_GPS = 'smartelock';
  filteredOptions: Observable<string[]>;
  public listGeneralGpsType: Observable<gpsType[]>;
  constructor(private gpsService: SelectGpsService, public utils: Utils) {
    this.getListGpsTypes();
  }

  ngOnInit() {
    this.options && this.options.initialValue ? this.gpsControl.setValue(this.options.initialValue) : this.gpsControl.setValue('');
    const validator = this.inputFormControl && this.inputFormControl.validator ? this.inputFormControl.validator({} as AbstractControl) : '';
    if (validator && validator.required) this.gpsControl.setValidators(Validators.required);
    this.gpsSub = this.gpsControl.valueChanges.subscribe(() => this.inputFormControl && this.inputFormControl.setValue(null));
    this.getListGpsProviders();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.validate && this.validate === 'touched') {
      if (this.inputFormControl && this.inputFormControl.invalid) this.gpsControl.markAsTouched();
    }
  }

  private getListGpsProviders() {
    this.gpsService.getGpsProviders().subscribe(
      (success: Array<{ monitor: boolean, name: string, companyId: string, id: string }>) => {
        if (success && Array.isArray(success) && success.length)
          this.gpsProviders = success;
        else this.gpsProviders = [];
      },
      (error) => this.gpsProviders = []
    );
  }

  getListGpsTypes() {
    this.gpsService.getAllTypesGps().subscribe(
      (success: any) => {
        this.gpsTypes = success.catalog;
        if (this.gpsTypes && this.gpsTypes.length) this.createInstanceAutocomplete();
        else this.gpsTypes = [];
      },
      (error) => this.gpsTypes = []
    );
  }

  private normalizeGpsName(name: string): string {
    return name ? name.toLowerCase().trim() : '';
  }

  public getGpsType(gpsName: string): { type: 'satrack' | 'monitor' | 'smartelock' | null, provider?: { monitor: boolean, name: string, companyId: string, id: string } | null } {
    if (!gpsName) return null;
    const normalizedName = this.normalizeGpsName(gpsName);
    if (normalizedName === this.SMARTELOCK_GPS) return { type: 'smartelock' };
    if (normalizedName === 'satrack') return { type: 'satrack' };
    if (this.gpsProviders && this.gpsProviders.length) {
      const provider = this.gpsProviders.find(p => p.name === gpsName);
      if (provider) return { type: 'monitor', provider: provider };
    }
    return { type: null };
  }

  public getGpsIntegrationStatus(gpsName: string): { isInProvider: boolean, integrationStatus: 'complete' | 'pending' | null } {
    if (!gpsName) return { isInProvider: false, integrationStatus: null };
    const gpsType = this.getGpsType(gpsName);
    if (gpsType.type === 'satrack' || gpsType.type === 'smartelock') return { isInProvider: true, integrationStatus: 'complete' };
    if (gpsType.type === 'monitor') {
      if (gpsType.provider && gpsType.provider.monitor === true && gpsType.provider.companyId) return { isInProvider: true, integrationStatus: 'complete' };
      else return { isInProvider: true, integrationStatus: 'pending' };
    }
    return { isInProvider: false, integrationStatus: null };
  }

  public getGpsImageInfo(gpsName: string): { src: string, alt: string } | null {
    const gpsType = this.getGpsType(gpsName);
    if (!gpsType) return null;

    const imageMap: Record<'satrack' | 'monitor' | 'smartelock', { src: string, alt: string }> = {
      satrack: { src: 'assets/svg/icons/satrack_logo.png', alt: 'Satrack' },
      monitor: { src: 'assets/svg/icons/isologo-monitor.png', alt: 'Monitor' },
      smartelock: { src: 'assets/svg/icons/smartelock-isotipo.png', alt: 'Smartelock' }
    };

    return imageMap[gpsType.type] || null;
  }

  public getIntegrationStatusColorClass(gpsName: string): string {
    const status = this.getGpsIntegrationStatus(gpsName);
    if (status.integrationStatus === 'complete') return 'integration-complete';
    if (status.integrationStatus === 'pending') return 'integration-pending';
    return '';
  }

  private createInstanceAutocomplete(): void {
    this.listGeneralGpsType = this.gpsControl.valueChanges.pipe(
      startWith(""),
      distinctUntilChanged(),
      map((value) => {
        if (value) {
          return this.gpsTypes.filter((option) => {
            return (
              option.name
                .toLowerCase()
                .indexOf(value.trim().toLowerCase().toString()) !== -1
            );
          });
        } else return [];

      })
    );
  }

  onSelectGps() {
    this.inputFormControl.setValue(this.gpsControl.value);
  }

  ngOnDestroy() {
    if (this.gpsSub) this.gpsSub.unsubscribe();
  }
}
